import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CheckItem, Entry } from '@app/models/_index';

import { FilterMatchType } from '@app/enums/filter-match-type.enum';
import { scrollIntoView } from '@app/utilities/_index';

@Component({
  selector: 'app-accordion',
  templateUrl: './accordion.component.html',
  styleUrls: ['./accordion.component.scss'],
  standalone: false,
})
export class AccordionComponent<T> implements OnInit, AfterViewInit {
  entry: Entry<T>[] = [];

  @Input() selectedCheckBox: string[]; // Check item
  @Input() selectedRadio: {};
  @Input() customAccordionContentCss: string;
  @Input() fragment: string;

  @Output() toggle = new EventEmitter<any>();

  @Input()
  set entries(entries: Entry<T>[]) {
    this.entry = entries;
  }

  get entries(): Entry<T>[] {
    return this.entry;
  }

  constructor() {}

  /**
   * Checks for any checked options during oninit and emits the results to the search page
   */
  ngOnInit(): void {
    // TODO: Move this a separate function in a future release, in case we need to call this outside
    // of OnInit
    this.entry &&
      this.entry.length > 0 &&
      this.entry.forEach((element) => {
        this.selectedCheckBox = [];
        const checkedOptions = element.array.filter((item) => item.checked);
        this.selectedCheckBox = checkedOptions.map((item) => item.value);
        // NOTE: This ensures anything previously checked from a search and any new checked items
        // gets properly emited and updated.
        if (this.selectedCheckBox.length > 0) {
          this.toggle.emit({ selectedCheckBox: this.selectedCheckBox, match: element.match });
        }
      });
  }

  /**
   * Accordion component AfterViewInit
   */
  ngAfterViewInit(): void {
    this.entry &&
      this.entry.length > 0 &&
      this.entry.forEach((element: Entry<T>) => {
        element.array.forEach((item) => {
          if (this.fragment == item.value) {
            this.scrollToAccordion(item.id);
          }
        });
      });
  }

  /**
   * Scroll to Accordion using fragment or link a href # alias
   */
  scrollToAccordion(id: string): void {
    if (this.fragment) {
      scrollIntoView('checkbox-id-' + id, 'center');
    }
  }

  /**
   * TODO: Move this out
   * Toggle accordion to expand collapse the filter category
   * @param index given accordion filter category item to show or hide
   */
  toggleAccordion(index: number): void {
    this.entries[index].isExpanded = !this.entries[index].isExpanded;
  }

  /**
   * TODO: Move this out.
   * Select all check items for each category filter item (entry match)
   * @param entryArray collection of check items
   * @param entryMatch name of the parent filter item for the checkable items
   */
  selectAll(entryArray: CheckItem[], entryMatch: FilterMatchType, isChecked?: boolean): void {
    entryArray &&
      entryArray.length > 0 &&
      entryArray.forEach((item) => {
        item.checked = isChecked;
      });
    this.onToggle(entryArray, entryMatch);

    // TODO: Emit event indicating select all is clicked
  }

  /**
   * TODO: Move this out.
   * Clear all checkable items for each category filter item (entry match)
   * @param entryArray collection of check items
   * @param entryMatch name of the parent filter item for the checkable items
   */
  clearAll(entryArray: CheckItem[], entryMatch: FilterMatchType): void {
    // NOTE: Call the existing select all but reset check items to be false
    this.selectAll(entryArray, entryMatch, false);
  }

  /**
   * Add Track By Accordion entries item to not redraw or
   * re-render list items that have not changed. This improves performance.
   * @param index - Index of accordion entries array
   * @param item - Accordion entries list item
   * @returns Accordion title
   */
  trackByEntriesTitle(index: number, item: Entry<T>): string {
    if (item) {
      return item.title;
    } else {
      return String(index);
    }
  }

  /**
   * Add Track By Entry Array title item to not redraw or
   * re-render list items that have not changed. This improves performance.
   * @param index - Index of accordion entries array
   * @param item - Accordion entries array list item
   * @returns Accordion title
   */
  trackByEntryArrayTitle(index: number, item: CheckItem): string {
    if (item) {
      return item.title;
    } else {
      return String(index);
    }
  }

  /**
   * TODO: Move this out.
   * On toggle or change filter the checked items and selected checkbox for matching entry match (filter category item)
   * and emit event for the calling component to handle the checked items
   * @param entryArray collection of check items
   * @param entryMatch name of the parent filter item for the checkable items
   */
  onToggle(entryArray: CheckItem[], entryMatch: FilterMatchType): void {
    this.selectedCheckBox = [];
    const checkedOptions = entryArray.filter((item) => item.checked);
    this.selectedCheckBox = checkedOptions.map((item) => item.value);
    // Emit accordion checked items to be handled by the calling component, which is the
    // search page
    this.toggle.emit({ selectedCheckBox: this.selectedCheckBox, match: entryMatch });
  }

  /**
   *  FilterMatchType enums based on route types
   */
  public get filterMatchTypes(): typeof FilterMatchType {
    return FilterMatchType;
  }
}
